import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import moment from 'moment';

import { Button, Flex, Icons, Surface, Typography } from '@jcm/design-system';
import { Layout, Row, Table, TableColumnType, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { SortOrder } from 'antd/es/table/interface';
import { alpha, Menu, MenuItem, MenuProps, styled } from '@mui/material';
import { Upgrade } from '@mui/icons-material';
import Lottie from 'lottie-react';

import { useGovernancaContext } from '../../../../../../context/GovernancaContext';

import TruncatedTextTooltip from '../../../../../../shared/components/truncatedTextTooltip/TruncatedTextTooltip';
import CustomAlert from '../../../../../../shared/components/customAlert/customAlert';
import ModalConfirmacao from '../../OperacoesEmMassa/ModalConfirmacao/ModalConfirmacao';

import LoadingListaObrigacoes from '../../../../../../assets/loadings/CarregandoListaObrigacoes.json';

import ObrigacoesService from '../../../services/Obrigacoes/Obrigacoes.service';
import { TApiResponse } from '../../../services/helpers/ApiResponse';
import { TObrigacaoLegal } from '../../../ts/types/ObrigacoesLegais';
import { PeriodicidadeReplicarObrigacaoEnum } from '../../../ts/enums/PeriodicidadeReplicarObrigacaoEnum';

import { createDateFilter, createDateOnFilter, createTextFilter, createTextOnFilter } from '../../Relatorios/filtros';

import GerenciarObrigacoesLegais from './CriarObrigacaoLegal/index';
import ObrigacoesTabMenuAbrapp from './ObrigacoesTabMenuAbrapp';
import estilos from './index.module.scss';
import { AbaObrigacoes } from '../../../ts/enums/AbaObrigacoesEnum';
import { TipoAcaoModal } from '../../../ts/enums/TipoAcaoModalEnum';

export type editandoObrigacaoLegal = {
	editando: boolean;
	obrigacaoLegal: TObrigacaoLegal;
};

const MenuElipse = styled((props: MenuProps) => (
	<Menu
		elevation={0}
		anchorOrigin={{
			vertical: 'bottom',
			horizontal: 'right',
		}}
		transformOrigin={{
			vertical: 'top',
			horizontal: 'right',
		}}
		{...props}
	/>
))(({ theme }) => ({
	'& .MuiPaper-root': {
		borderRadius: 6,
		marginTop: theme.spacing(1),
		minWidth: 180,
		color: 'rgb(55, 65, 81)',
		boxShadow:
			'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
		'& .MuiMenu-list': {
			padding: '4px 0',
		},
		'& .MuiMenuItem-root': {
			'& .MuiSvgIcon-root': {
				fontSize: 18,
				color: theme.palette.text.secondary,
				marginRight: theme.spacing(1.5),
			},
			'&:active': {
				backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
			},
		},
		...theme.applyStyles('dark', {
			color: theme.palette.grey[300],
		}),
	},
}));

const ListagemObrigacoesLegais: React.FC = () => {
	const [obrigacoes, setObrigacoes] = useState<TObrigacaoLegal[]>([]);
	const { empresaAtiva } = useGovernancaContext();
	const [carregando, setCarregando] = useState<boolean>(true);
	const [obrigacaoSelecionada, setObrigacaoSelecionada] = useState<TObrigacaoLegal>();
	const [editandoObrigacaoLegal, setEditandoObrigacaoLegal] = useState<editandoObrigacaoLegal>({
		editando: false,
		obrigacaoLegal: {
			id: 0,
			codigo: '',
			descricao: '',
			fundamentacaoLegal: '',
			prazoLegal: '',
			temporalidade: '',
			formaCumprimento: '',
			periodicidade: 0,
			dataVencimento: '',
			datas: '',
			ativa: true,
		},
	});
	const [isSiderOpen, setSiderOpen] = useState(false);
	const [elementoAncoraMenu, setElementoAncoraMenu] = React.useState<null | HTMLElement>(null);
	const [alert, setAlert] = useState<{ id: number; type: 'success' | 'error'; content: string; title?: string } | null>(
		null,
	);
	const [modalConfirmacaoVisible, setModalConfirmacaoVisible] = useState(false);
	const [modalAcao, setModalAcao] = useState<TipoAcaoModal | null>(null);
	const [obrigacaoParaAcao, setObrigacaoParaAcao] = useState<TObrigacaoLegal | null>(null);
	const [loadingAcao, setLoadingAcao] = useState(false);
	const [abaAtiva, setAbaAtiva] = useState<AbaObrigacoes>(AbaObrigacoes.Todas);

	const isExcluidaTab = abaAtiva === AbaObrigacoes.Excluidas;

	const columnNames: Record<string, string> = {
		codigo: 'Código',
		descricao: 'Descrição',
		fundamentacaoLegal: 'Fundamentação legal',
		prazoLegal: 'Prazo legal',
		temporalidade: 'Temporalidade',
		formaCumprimento: 'Forma de cumprimento',
		periodicidade: 'Periodicidade',
		dataVencimento: 'Vencimento',
		datas: 'Datas',
	};

	const periodicidadeLabels: Record<PeriodicidadeReplicarObrigacaoEnum, string> = {
		[PeriodicidadeReplicarObrigacaoEnum.Unica]: 'Única',
		[PeriodicidadeReplicarObrigacaoEnum.Diaria]: 'Diária',
		[PeriodicidadeReplicarObrigacaoEnum.Semanal]: 'Semanal',
		[PeriodicidadeReplicarObrigacaoEnum.Quinzenal]: 'Quinzenal',
		[PeriodicidadeReplicarObrigacaoEnum.Mensal]: 'Mensal',
		[PeriodicidadeReplicarObrigacaoEnum.Bimestral]: 'Bimestral',
		[PeriodicidadeReplicarObrigacaoEnum.Trimestral]: 'Trimestral',
		[PeriodicidadeReplicarObrigacaoEnum.Quadrimestral]: 'Quadrimestral',
		[PeriodicidadeReplicarObrigacaoEnum.Semestral]: 'Semestral',
		[PeriodicidadeReplicarObrigacaoEnum.Anual]: 'Anual',
		[PeriodicidadeReplicarObrigacaoEnum.Decendial]: 'Decendial',
		[PeriodicidadeReplicarObrigacaoEnum.QuandoAplicavel]: 'Quando Aplicável',
		[PeriodicidadeReplicarObrigacaoEnum.NA]: 'N/A',
	};

	const formatDate = (value: string | undefined) => {
		return value ? moment(value).format('DD/MM/YYYY') : 'Sem informação';
	};

	async function subirAtualizacaoObrigacaoLegal(codigoObrigacao: string): Promise<{ isSuccess: boolean }> {
		try {
			const response = await _obrigacoesService.putAtualizarObrigacoesAbrappEmMassa(
				empresaAtiva ?? '',
				codigoObrigacao,
			);

			return { isSuccess: response.isSuccess ?? false }; 
		} catch (error) {
			console.error('Erro ao atualizar obrigação:', error);
			return { isSuccess: false }; 
		}
	}

	const excluirObrigacaoLegalAbrapp = async (idObrigacao: number) => {
		if (!empresaAtiva) {
			console.error('Erro: empresaAtiva não está definida.');
			return;
		}

		setLoadingAcao(true);

		try {
			const response = await _obrigacoesService.excluirObrigacaoLegalAbrapp(empresaAtiva, idObrigacao);

			if (response?.isSuccess) {
				setAlert({
					id: Date.now(),
					type: 'success',
					content: 'Obrigação excluída com sucesso!',
					title: 'Sucesso',
				});

				fetchAllObrigacoesData();
			} else {
				throw new Error('A exclusão não foi bem-sucedida.');
			}
		} catch (error) {
			console.error('Erro ao excluir a obrigação:', error);

			setAlert({
				id: Date.now(),
				type: 'error',
				content: 'Erro ao excluir a obrigação.',
				title: 'Erro',
			});
		} finally {
			setLoadingAcao(false);
			setModalConfirmacaoVisible(false);
		}
	};

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setElementoAncoraMenu(event.currentTarget);
	};

	const fecharMenuElipse = () => {
		setElementoAncoraMenu(null);
	};

	const alterarAbaObrigacoesFiltradas = (tabKey: string) => {
		const abaSelecionada = Object.values(AbaObrigacoes).includes(tabKey as AbaObrigacoes)
			? (tabKey as AbaObrigacoes)
			: abaAtiva;

		setAbaAtiva(abaSelecionada);
	};

	const alternarMenuLateral = () => {
		setSiderOpen((prev: boolean) => !prev);
	};

	const executarAcaoEmMassa = async () => {
		if (!obrigacaoParaAcao || modalAcao !== TipoAcaoModal.Subir) return;
	
		setLoadingAcao(true);
	
		try {
			const response = await subirAtualizacaoObrigacaoLegal(obrigacaoParaAcao.codigo);
	
			if (response.isSuccess) {
				setAlert({
					id: Date.now(),
					type: 'success',
					content: 'Operação concluída com sucesso!',
					title: 'Sucesso',
				});
	
				setTimeout(() => {
					setModalConfirmacaoVisible(false);
					setObrigacaoParaAcao(null);
				}, 1000); 
			} else {
				throw new Error('A atualização não foi bem-sucedida.');
			}
		} catch (error) {
			setAlert({
				id: Date.now(),
				type: 'error',
				content: 'Erro ao executar a operação.',
				title: 'Erro',
			});
		} finally {
			setTimeout(() => {
				setLoadingAcao(false);
			}, 1200); 
		}
	};
	

	const colunasFixas: TableColumnType<TObrigacaoLegal>[] = [
		{
			title: columnNames['codigo'],
			dataIndex: 'codigo',
			width: 120,
			ellipsis: true,
			key: '1',
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => Number(a.id) - Number(b.id),
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			render: (text: number | undefined) => text || 'Sem informação',
			filterDropdown: createTextFilter('Buscar código'),
			onFilter: createTextOnFilter('codigo'),
			fixed: 'left',
		},
		{
			title: columnNames['descricao'],
			dataIndex: 'descricao',
			width: 200,
			ellipsis: true,
			key: '2',
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => a.descricao?.localeCompare(b.descricao || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar descricao'),
			onFilter: createTextOnFilter('descricao'),
		},
		{
			title: columnNames['fundamentacaoLegal'],
			dataIndex: 'fundamentacaoLegal',
			width: 200,
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) =>
				a.fundamentacaoLegal?.localeCompare(b.fundamentacaoLegal || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar fundamentação legal'),
			onFilter: createTextOnFilter('fundamentacaoLegal'),
			key: '3',
		},
		{
			title: columnNames['prazoLegal'],
			dataIndex: 'prazoLegal',
			width: 200,
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => a.prazoLegal?.localeCompare(b.prazoLegal || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar prazo legal'),
			onFilter: createTextOnFilter('prazoLegal'),
			key: '4',
		},
		{
			title: columnNames['temporalidade'],
			dataIndex: 'temporalidade',
			width: 200,
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => a.temporalidade?.localeCompare(b.temporalidade || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar temporalidade'),
			onFilter: createTextOnFilter('temporalidade'),
			key: '5',
		},
		{
			title: columnNames['formaCumprimento'],
			dataIndex: 'formaCumprimento',
			width: 200,
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) =>
				a.formaCumprimento?.localeCompare(b.formaCumprimento || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar forma de cumprimento'),
			onFilter: createTextOnFilter('formaCumprimento'),
			key: '6',
		},
		{
			title: columnNames['periodicidade'],
			dataIndex: 'periodicidade',
			width: 200,
			render: (value: PeriodicidadeReplicarObrigacaoEnum) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={periodicidadeLabels[value] || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => (a.periodicidade || 0) - (b.periodicidade || 0),
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar periodicidade'),
			onFilter: createTextOnFilter('periodicidade'),
			key: '7',
		},
		{
			title: columnNames['dataVencimento'],
			dataIndex: 'dataVencimento',
			width: 160,
			render: (text: string | undefined) => formatDate(text),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) =>
				dayjs(a.dataVencimento).isBefore(dayjs(b.dataVencimento)) ? -1 : 1,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createDateFilter(),
			onFilter: createDateOnFilter('dataVencimento'),
			key: '8',
		},
		{
			title: columnNames['datas'],
			dataIndex: 'datas',
			width: 200,
			render: (text: string | undefined) => (
				<div className={estilos.maxWidth}>
					<TruncatedTextTooltip text={text || 'Sem informação'} />
				</div>
			),
			sorter: (a: TObrigacaoLegal, b: TObrigacaoLegal) => a.datas?.localeCompare(b.datas || '') || 0,
			sortDirections: ['descend', 'ascend'] as SortOrder[],
			filterDropdown: createTextFilter('Buscar datas'),
			onFilter: createTextOnFilter('datas'),
			key: '9',
		},
		{
			title: '',
			render: (a: TObrigacaoLegal) => (
				<Tooltip title='Ver mais' mouseLeaveDelay={0}>
					<Button
						circle
						className={estilos.tamanhoIcone}
						type='text'
						variant='default'
						icon={<MoreOutlined />}
						onClick={(event) => {
							setObrigacaoSelecionada(a);
							handleClick(event);
						}}
						aria-controls={abrirMenuElipse ? 'demo-customized-menu' : undefined}
						aria-haspopup='true'
						aria-expanded={abrirMenuElipse ? 'true' : undefined}
					/>
				</Tooltip>
			),
			width: 80,
			key: '10',
			fixed: 'right',
		},
	];

	useEffect(() => {
		if (empresaAtiva) {
			fetchAllObrigacoesData();
		}
	}, [empresaAtiva]);

	const _obrigacoesService = new ObrigacoesService();

	const fetchAllObrigacoesData = async () => {
		try {
			if (empresaAtiva !== null) {
				const obrigacaoLegalResponse: TApiResponse<TObrigacaoLegal[]> =
					await _obrigacoesService.getTodasObrigacoesLegais(empresaAtiva);
				setObrigacoes(obrigacaoLegalResponse.data.result);
			}
		} catch (error) {
			console.error('Erro ao buscar obrigações:', error);
		} finally {
			setCarregando(false);
		}
	};

	const abrirMenuElipse = Boolean(elementoAncoraMenu);

	return (
		<Layout className={estilos.container}>
			<Row>
				<MenuElipse
					id='demo-customized-menu'
					MenuListProps={{
						'aria-labelledby': 'demo-customized-button',
					}}
					anchorEl={elementoAncoraMenu}
					open={abrirMenuElipse}
					onClose={fecharMenuElipse}
				>
					<MenuItem
						onClick={() => {
							if (obrigacaoSelecionada) {
								setEditandoObrigacaoLegal({ editando: true, obrigacaoLegal: obrigacaoSelecionada });
								alternarMenuLateral();
								fecharMenuElipse();
							}
						}}
						disableRipple
						disabled={isExcluidaTab}
					>
						<Flex gap={4} align='center'>
							<Icons.Edit style={{ fontSize: '20px' }} />
							Editar
						</Flex>
					</MenuItem>
					<MenuItem onClick={fecharMenuElipse} disableRipple disabled={true}>
						<Flex gap={4} align='center'>
							<Icons.Delete style={{ fontSize: '20px' }} />
							Desativar
						</Flex>
					</MenuItem>
					<MenuItem
						onClick={() => {
							if (obrigacaoSelecionada) {
								setObrigacaoParaAcao(obrigacaoSelecionada);
								setModalAcao(TipoAcaoModal.Subir);
								setModalConfirmacaoVisible(true);
							}
							fecharMenuElipse();
						}}
						disableRipple
						disabled={isExcluidaTab}
					>
						<Flex align='center'>
							<Upgrade style={{ fontSize: '20px' }} />
							Atualizar em massa
						</Flex>
					</MenuItem>
					<MenuItem
						onClick={() => {
							if (obrigacaoSelecionada) {
								setObrigacaoParaAcao(obrigacaoSelecionada);
								setModalAcao(TipoAcaoModal.Excluir);
								setModalConfirmacaoVisible(true);
							}
							fecharMenuElipse();
						}}
						disableRipple
						disabled={isExcluidaTab}
					>
						<Flex gap={4} align='center'>
							<Icons.Delete style={{ fontSize: '20px' }} />
							Excluir
						</Flex>
					</MenuItem>
				</MenuElipse>
				<Flex className={estilos.cabecalho}>
					<Typography.Title size='large'>Obrigações legais</Typography.Title>
					<Button
						type='filled-tonal'
						variant='default'
						className={estilos.novaObrigacao}
						onClick={() => {
							alternarMenuLateral();
							setEditandoObrigacaoLegal({
								editando: false,
								obrigacaoLegal: {
									id: 0,
									codigo: '',
									descricao: '',
									fundamentacaoLegal: '',
									prazoLegal: '',
									temporalidade: '',
									formaCumprimento: '',
									periodicidade: 0,
									dataVencimento: '',
									datas: '',
									ativa: true,
								},
							});
						}}
					>
						Nova Obrigação
					</Button>
				</Flex>
				<Surface className={estilos.containerRelatorios}>
					<ObrigacoesTabMenuAbrapp
						todasObrigacoes={obrigacoes.filter((obrigacao: TObrigacaoLegal) => obrigacao.ativa).length}
						excluidas={obrigacoes.filter((obrigacao: TObrigacaoLegal) => !obrigacao.ativa).length}
						activeTabKey={abaAtiva}
						onTabChange={alterarAbaObrigacoesFiltradas}
						loading={carregando}
					/>

					<Table
						dataSource={
							abaAtiva === AbaObrigacoes.Todas
								? obrigacoes.filter((obrigacao: TObrigacaoLegal) => obrigacao.ativa)
								: obrigacoes.filter((obrigacao: TObrigacaoLegal) => !obrigacao.ativa)
						}
						columns={colunasFixas}
						pagination={{ position: ['bottomCenter'] }}
						rowKey='id'
						scroll={{ x: 'max-content', y: 600 }}
						expandable={{ showExpandColumn: false }}
						loading={carregando}
					/>
				</Surface>
			</Row>

			<div>
				<GerenciarObrigacoesLegais
					open={isSiderOpen}
					onClose={() => setSiderOpen(false)}
					setObrigacoes={setObrigacoes}
					editandoObrigacaoLegal={editandoObrigacaoLegal}
				/>
			</div>
			{alert && <CustomAlert key={alert.id} type={alert.type} content={alert.content} title={alert.title} />}

			{modalConfirmacaoVisible && (
				<ModalConfirmacao
					visivel={modalConfirmacaoVisible}
					onConfirm={() => {
						if (!loadingAcao) {
							if (modalAcao === TipoAcaoModal.Excluir && obrigacaoParaAcao) {
								excluirObrigacaoLegalAbrapp(obrigacaoParaAcao.id);
							} else {
								executarAcaoEmMassa();
							}
						}
					}}
					onCancel={() => {
						if (!loadingAcao) {
							setModalConfirmacaoVisible(false);
							setObrigacaoParaAcao(null);
						}
					}}
					titulo={modalAcao === 'excluir' ? 'Confirmação de Exclusão' : 'Confirmação de Atualização'}
					descricao={
						loadingAcao ? (
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '150px' }}>
								<Lottie loop animationData={LoadingListaObrigacoes} style={{ width: '100%', height: '100%' }} />
							</div>
						) : (
							<div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
								<p>
									Você está prestes a <strong>{modalAcao === 'excluir' ? 'excluir' : 'atualizar'}</strong> esta
									obrigação. Essa alteração <strong>não poderá ser desfeita</strong>. Tem certeza?
								</p>
							</div>
						)
					}
					confirmButtonText={modalAcao === TipoAcaoModal.Excluir ? 'Excluir' : 'Atualizar'}
					confirmButtonDisabled={loadingAcao}
					cancelButtonDisabled={loadingAcao}
					hideCheckbox={false}
					customCheckboxLabel={
						modalAcao === TipoAcaoModal.Excluir
							? 'Sim, desejo excluir esta obrigação permanentemente.'
							: 'Sim, desejo atualizar esta obrigação para todas as entidades.'
					}
				/>
			)}
		</Layout>
	);
};
export default ListagemObrigacoesLegais;
